import { EventStatus } from '@puzzlehq/sdk';
import { useArcadeStoreSKU } from '@src/hooks/useArcadeStore';
import { zodArcadeStoreOrder } from 'jigsaw';
import { Text, Skeleton, VStack, HStack, Progress, theme, Link } from 'ui_2';
import { z } from 'zod';
import dayjs from 'dayjs';
import { BiInfoCircle } from 'react-icons/bi';

export type ArcadeStoreOrder = z.infer<typeof zodArcadeStoreOrder> & {
  fulfillmentDate?: string | Date;
  purchaseDate?: string | Date;
};

enum OrderProgress {
  _01_PurchaseCreating = 10,
  _02_PurchasePending = 28,
  _03_PurchaseSettled = 46,
  _04_FulfillmentCreating = 64,
  _05_FulfillmentPending = 82,
  _06_FulfillmentSettled = 100,
}

const getOrderProgress = (order: ArcadeStoreOrder): OrderProgress => {
  const { purchaseStatus, fulfillmentStatus } = order;

  // Check fulfillment status first
  if (fulfillmentStatus === EventStatus.Settled)
    return OrderProgress._06_FulfillmentSettled;
  if (fulfillmentStatus === EventStatus.Pending)
    return OrderProgress._05_FulfillmentPending;
  if (fulfillmentStatus === EventStatus.Creating)
    return OrderProgress._04_FulfillmentCreating;

  // If no fulfillment status, check purchase status
  if (purchaseStatus === EventStatus.Settled)
    return OrderProgress._03_PurchaseSettled;
  if (purchaseStatus === EventStatus.Pending)
    return OrderProgress._02_PurchasePending;
  if (purchaseStatus === EventStatus.Creating)
    return OrderProgress._01_PurchaseCreating;

  // Default fallback
  return OrderProgress._01_PurchaseCreating;
};

// Helper function to get human-readable status text
const getStatusText = (order: ArcadeStoreOrder): string => {
  const { purchaseStatus, fulfillmentStatus } = order;

  if (fulfillmentStatus === EventStatus.Settled)
    return `Completed. Your purchase will be available soon.`;
  if (fulfillmentStatus === EventStatus.Pending) return 'Fulfilling Order';
  if (fulfillmentStatus === EventStatus.Creating)
    return 'Preparing Fulfillment';

  if (purchaseStatus === EventStatus.Settled) return 'Payment Confirmed';
  if (purchaseStatus === EventStatus.Pending) return 'Processing Payment';
  if (purchaseStatus === EventStatus.Creating) return 'Initializing Order';

  return 'Processing';
};

// Helper function to get the appropriate date display
const getDisplayDate = (order: ArcadeStoreOrder): string | null => {
  if (order.fulfilled) {
    return dayjs(order.fulfilled).format('MMM D, YYYY • h:mm A');
  } else if (order.purchased) {
    return dayjs(order.purchased).format('MMM D, YYYY • h:mm A');
  }
  return null;
};

// Helper function to check if order has been processing for over 10 minutes
const isLongPendingOrder = (order: ArcadeStoreOrder): boolean => {
  if (order.fulfillmentStatus === EventStatus.Settled) {
    return false; // Completed orders don't need help message
  }

  const orderDate = order.purchased ? new Date(order.purchased) : null;
  if (!orderDate) return false;

  const tenMinutesAgo = new Date();
  tenMinutesAgo.setMinutes(tenMinutesAgo.getMinutes() - 10);

  return orderDate < tenMinutesAgo;
};

type PendingOrderRowProps = {
  order: ArcadeStoreOrder;
};
export const PendingOrderRow = ({ order }: PendingOrderRowProps) => {
  const { data, isLoading } = useArcadeStoreSKU({ skuId: order.skuId });
  const smallImageURL = data?.smallImageURL ?? data?.service.smallLogoImageURL;

  const progress = getOrderProgress(order);
  const { fulfillmentError, purchaseError } = order;
  const hasError = !!fulfillmentError || !!purchaseError;
  const statusText = getStatusText(order);
  const displayDate = getDisplayDate(order);
  const showDiscordHelp = hasError || isLongPendingOrder(order);

  return (
    <VStack className='w-full rounded-[10px] border border-gray-300 p-2 shadow-[0px_1px_4px_0px_rgba(0,0,0,0.08)] sm:p-3'>
      <HStack className='w-full items-start gap-2 sm:gap-3'>
        {isLoading && (
          <Skeleton className='h-10 w-10 flex-shrink-0 rounded-[10px] sm:h-[44px] sm:w-[44px]' />
        )}
        {smallImageURL && (
          <img
            src={smallImageURL}
            alt={`${data?.service.title} logo`}
            width={44}
            height={44}
            className='h-10 w-10 flex-shrink-0 rounded-[10px] object-cover sm:h-[44px] sm:w-[44px]'
          />
        )}
        <VStack className='min-w-0 flex-1 space-y-1'>
          <div className='flex w-full flex-col gap-1'>
            <Text font='medium-md' className='break-words text-sm sm:text-base'>
              {order.skuTitle}
            </Text>
            {displayDate && (
              <Text className='text-xs text-gray-500'>{displayDate}</Text>
            )}
          </div>
          <HStack className='items-baseline gap-2'>
            <div
              className={`h-2 w-2 flex-shrink-0 rounded-full ${
                hasError
                  ? 'bg-red-400'
                  : progress === OrderProgress._06_FulfillmentSettled
                  ? 'bg-green-600'
                  : 'bg-primary-yellow'
              }`}
            ></div>
            <Text
              className={`w-fit text-xs sm:text-sm ${
                hasError
                  ? 'text-red-400'
                  : progress === OrderProgress._06_FulfillmentSettled
                  ? 'text-green-600'
                  : 'text-primary-yellow'
              }`}
            >
              {hasError
                ? `Error: ${fulfillmentError ?? purchaseError}`
                : statusText}
            </Text>
          </HStack>
        </VStack>
      </HStack>

      {/* Progress bar */}
      {!hasError && (
        <VStack className='mt-2 w-full sm:mt-3'>
          <Progress value={progress} color={theme.colors.green[600]} />
        </VStack>
      )}

      {/* Discord help message for long-pending orders */}
      {showDiscordHelp && (
        <div className='mt-2 w-full rounded-md border border-gray-200 bg-gray-50 p-2 text-sm sm:mt-3'>
          <div className='flex flex-col gap-2'>
            {!hasError && (
              <HStack className='items-center gap-2'>
                <BiInfoCircle
                  color={theme.colors.gray[600]}
                  size={16}
                  className='flex-shrink-0'
                />
                <Text className='text-xs text-gray-600 sm:text-sm'>
                  Your order is taking longer than expected.
                </Text>
              </HStack>
            )}
            <Link
              href='https://discord.gg/puzzlearcade'
              target='_blank'
              className='text-primary-blue text-xs hover:underline sm:text-sm'
            >
              Get help on our Discord
            </Link>
          </div>
        </div>
      )}
    </VStack>
  );
};
